/* @jsx h */
import { defineComponent, h } from "vue";
import dayjs from "dayjs";
import { useLocalization } from "../Localization";
import { TimeField } from "./TimeField";
import type { TimeOfDay } from "./types";

export type RangeEnd = "start" | "end";

interface RangeFieldsProps {
  startDate: string;
  endDate: string;
  startTime: TimeOfDay;
  endTime: TimeOfDay;
  /** Which end the next calendar tap will set. */
  active: RangeEnd;
  showTime: boolean;
  onActivate: (end: RangeEnd) => void;
  onStartTimeChange: (value: TimeOfDay) => void;
  onEndTimeChange: (value: TimeOfDay) => void;
}

/**
 * The From/To header. Doubles as the selection-state indicator: the outlined
 * field is the one the next calendar tap writes to, which is what the old
 * hidden `selecting` flag used to convey via a footer that scrolled off screen
 * on mobile.
 */
export const RangeFields = defineComponent<RangeFieldsProps>({
  name: "RangeFields",
  props: [
    "startDate",
    "endDate",
    "startTime",
    "endTime",
    "active",
    "showTime",
    "onActivate",
    "onStartTimeChange",
    "onEndTimeChange",
  ],
  setup(props) {
    const { t, locale } = useLocalization();

    const formatDate = (date: string) =>
      new Intl.DateTimeFormat(locale.value || "en", {
        day: "numeric",
        month: "short",
        year: "numeric",
      }).format(dayjs(date).toDate());

    const renderField = (end: RangeEnd) => {
      const isActive = props.active === end;
      const date = end === "start" ? props.startDate : props.endDate;
      const time = end === "start" ? props.startTime : props.endTime;
      const label =
        end === "start"
          ? t("date_range_picker.modal.from")
          : t("date_range_picker.modal.to");

      return (
        <div
          role="button"
          tabindex={0}
          onClick={() => props.onActivate(end)}
          onKeydown={(e: KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              props.onActivate(end);
            }
          }}
          class={[
            "flex-1 min-w-0 cursor-pointer rounded-xl px-2.5 py-2 text-left",
            "border transition-colors duration-150",
            "",
            isActive
              ? "bg-primary/[0.07]"
              : "bg-base-200/30 hover:bg-base-200/60",
          ].join(" ")}
        >
          <div class="text-[10px] font-semibold uppercase tracking-wider text-base-content/40">
            {label}
          </div>
          <div class="truncate pt-0.5 text-sm font-semibold text-base-content">
            {formatDate(date)}
          </div>
          {/* Stop propagation so focusing the time input doesn't retarget the
              calendar to this end of the range. */}
          {props.showTime && (
            <div onClick={(e: Event) => e.stopPropagation()} class="mt-1.5">
              <TimeField
                value={time}
                ariaLabel={label}
                onChange={
                  end === "start"
                    ? props.onStartTimeChange
                    : props.onEndTimeChange
                }
              />
            </div>
          )}
        </div>
      );
    };

    return () => (
      <div class="flex items-stretch gap-2">
        {renderField("start")}
        <div class="flex shrink-0 items-center text-base-content/25">
          <span class="material-symbols-outlined text-base">arrow_forward</span>
        </div>
        {renderField("end")}
      </div>
    );
  },
});
